import React, { useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  FileCode,
  Tag,
  Filter,
  Search,
  Plus,
  AlertCircle,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { CodeHistoryCard } from "@/components/dashboard/CodeHistoryCard";
import { extractUniqueItems } from "@/utils/helpers";
import { useGeneratedCodeFilters } from "@/hooks/useGeneratedCodeFilters";
import { useGeneratedCodes } from "@/hooks/useGeneratedCodes";
import { useDebounce } from "@/hooks/useDebounce";
import { getRubriqueLabel } from "@/data/silae-rubriques";

interface CodeRepositoryViewProps {
  onEdit: (codeId: string, hasGeneratedCode: boolean) => void;
  onCreateNew?: () => void;
}

/**
 * CodeRepositoryView
 *
 * Composant optimisé pour afficher et gérer la liste des codes générés.
 * Utilise des hooks personnalisés pour la gestion d'état et des performances.
 */
export const CodeRepositoryView: React.FC<CodeRepositoryViewProps> = ({
  onEdit,
  onCreateNew,
}) => {
  const { t } = useTranslation();

  // Hooks personnalisés
  const { codes, isLoading, error, loadCodes, reloadCodes } =
    useGeneratedCodes();
  const {
    searchQuery,
    rubriqueFilter,
    tagFilter,
    showTemplatesOnly,
    setSearchQuery,
    setRubriqueFilter,
    setTagFilter,
    setShowTemplatesOnly,
    filters,
    hasActiveFilters,
    resetFilters,
  } = useGeneratedCodeFilters();

  // Debounce la recherche pour optimiser les performances
  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  // Extract unique rubriques from codes (optimized with useMemo)
  const uniqueRubriques = useMemo(
    () => extractUniqueItems(codes, (code) => code.rubriques),
    [codes],
  );

  // Extract unique tags from codes (optimized with useMemo)
  const uniqueTags = useMemo(
    () => extractUniqueItems(codes, (code) => code.tags),
    [codes],
  );

  // Charger les codes au montage et lors des changements de filtres
  useEffect(() => {
    const filtersWithSearch = {
      ...filters,
      search: debouncedSearchQuery || undefined,
    };

    loadCodes(filtersWithSearch, t("history.errors.loadFailed"));
  }, [debouncedSearchQuery, filters, loadCodes, t]);

  // Handle code deletion - memoized to prevent unnecessary re-renders
  const handleDelete = useCallback(() => {
    const filtersWithSearch = {
      ...filters,
      search: debouncedSearchQuery || undefined,
    };
    reloadCodes(filtersWithSearch);
  }, [filters, debouncedSearchQuery, reloadCodes]);

  // Handle code update - memoized to prevent unnecessary re-renders
  const handleUpdate = useCallback(() => {
    const filtersWithSearch = {
      ...filters,
      search: debouncedSearchQuery || undefined,
    };
    reloadCodes(filtersWithSearch);
  }, [filters, debouncedSearchQuery, reloadCodes]);

  return (
    <div className="space-y-6">
      {/* Filters Section */}

      <div className="flex flex-col sm:flex-row gap-3">
        {/* Search */}
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground z-10" />
          <Input
            type="text"
            placeholder={t("history.searchPlaceholder")}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 pr-9"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery("")}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors z-10"
              aria-label="Effacer la recherche"
              type="button"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* Rubriques Filter */}
        <Select value={rubriqueFilter} onValueChange={setRubriqueFilter}>
          <SelectTrigger className="w-full sm:w-[200px]">
            <Filter className="h-4 w-4 mr-2" />
            <SelectValue placeholder={t("history.filters.allRubriques")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">
              {t("history.filters.allRubriques")}
            </SelectItem>
            {uniqueRubriques.map((rubrique) => (
              <SelectItem key={rubrique} value={rubrique}>
                <span className="font-mono font-bold mr-2">{rubrique}</span>
                <span>{getRubriqueLabel(rubrique)}</span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Tags Filter */}
        <Select value={tagFilter} onValueChange={setTagFilter}>
          <SelectTrigger className="w-full sm:w-[200px]">
            <Tag className="h-4 w-4 mr-2" />
            <SelectValue placeholder={t("history.filters.allTags")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("history.filters.allTags")}</SelectItem>
            {uniqueTags.map((tag) => (
              <SelectItem key={tag} value={tag}>
                {tag}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Templates Filter */}
        <Button
          variant={showTemplatesOnly ? "default" : "outline"}
          onClick={() => setShowTemplatesOnly(!showTemplatesOnly)}
          className="w-full sm:w-auto h-[40px]"
        >
          <FileCode className="h-4 w-4 mr-2" />
          {t("history.filters.templatesOnly")}
        </Button>

        {/* Reset Filters Button */}
        {hasActiveFilters && (
          <Button
            variant="ghost"
            onClick={resetFilters}
            className="w-full sm:w-auto"
            title={t("history.filters.reset")}
          >
            <X className="h-4 w-4 mr-2" />
            {t("history.filters.reset")}
          </Button>
        )}

        {/* Create New Code Button */}
        {onCreateNew && (
          <Button onClick={onCreateNew} size="lg" className="w-full sm:w-auto">
            <Plus className="h-4 w-4 mr-2" />
            {t("codeGenerator.createNewCode")}
          </Button>
        )}
      </div>

      {/* Active Filters Display */}
      {hasActiveFilters && (
        <>
          <Separator className="my-4" />
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm text-muted-foreground">
              {t("history.filters.active", "Filtres actifs")}:
            </span>
            {searchQuery && (
              <Badge variant="outline" className="gap-1">
                <Search className="h-3 w-3" />
                {searchQuery}
                <button
                  onClick={() => setSearchQuery("")}
                  className="ml-1 hover:text-destructive"
                  aria-label="Retirer le filtre de recherche"
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            )}
            {rubriqueFilter !== "all" && (
              <Badge variant="outline" className="gap-1">
                <Filter className="h-3 w-3" />
                <span className="font-mono font-bold mr-1">
                  {rubriqueFilter}
                </span>
                <span>{getRubriqueLabel(rubriqueFilter)}</span>
                <button
                  onClick={() => setRubriqueFilter("all")}
                  className="ml-1 hover:text-destructive"
                  aria-label="Retirer le filtre rubrique"
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            )}
            {tagFilter !== "all" && (
              <Badge variant="outline" className="gap-1">
                <Tag className="h-3 w-3" />
                {tagFilter}
                <button
                  onClick={() => setTagFilter("all")}
                  className="ml-1 hover:text-destructive"
                  aria-label="Retirer le filtre tag"
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            )}
            {showTemplatesOnly && (
              <Badge variant="outline" className="gap-1">
                <FileCode className="h-3 w-3" />
                {t("history.filters.templatesOnly")}
                <button
                  onClick={() => setShowTemplatesOnly(false)}
                  className="ml-1 hover:text-destructive"
                  aria-label="Retirer le filtre templates"
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            )}
          </div>
        </>
      )}

      {/* Error Alert */}
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Loading State */}
      {isLoading && (
        <div className="flex flex-wrap gap-4 justify-center">
          {Array.from({ length: 6 }).map((_, i) => (
            <Card
              key={i}
              className="group hover:shadow-lg transition-all duration-200 border-l-4 min-w-[300px] max-w-[600px] w-full border-l-primary dark:border-l-blue-500"
            >
              <CardHeader className="">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0 flex justify-between flex-row">
                    {/* Title with icon */}
                    <CardTitle className="text-lg flex items-center gap-2 flex-wrap">
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        <Skeleton className="h-5 w-5 shrink-0 rounded" />
                        <Skeleton className="h-6 w-48" />
                      </div>
                      {/* Template badge skeleton (randomly show for variety) */}
                      {i === 1 && (
                        <Skeleton className="h-5 w-5 ml-auto shrink-0 rounded-full" />
                      )}
                    </CardTitle>

                    {/* Tags skeleton */}
                    <div className="flex items-center gap-2">
                      <Skeleton className="h-5 w-16 rounded-full" />
                      <Skeleton className="h-5 w-20 rounded-full" />
                    </div>
                  </div>

                  {/* Actions menu skeleton */}
                  <Skeleton className="h-8 w-8 shrink-0 rounded" />
                </div>
              </CardHeader>

              {/* Rubriques skeleton */}
              <div className="px-4 space-y-2">
                <Skeleton className="h-3 w-24" />
                <div className="flex flex-wrap gap-2">
                  <Skeleton className="h-6 w-32" />
                  <Skeleton className="h-6 w-40" />
                  {i % 2 === 0 && <Skeleton className="h-6 w-24" />}
                </div>
              </div>

              <CardContent className="space-y-4 pt-0 grow">
                {/* Description skeleton */}
                <div className="space-y-2">
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-3/4" />
                </div>
              </CardContent>

              <CardFooter>
                {/* Metadata skeleton */}
                <div className="flex flex-wrap items-center gap-3">
                  <div className="flex items-center gap-1.5">
                    <Skeleton className="h-3.5 w-3.5 rounded" />
                    <Skeleton className="h-4 w-24" />
                  </div>
                  {i !== 3 && (
                    <div className="flex items-center gap-1.5">
                      <Skeleton className="h-3.5 w-3.5 rounded" />
                      <Skeleton className="h-4 w-20" />
                    </div>
                  )}
                </div>
              </CardFooter>
            </Card>
          ))}
        </div>
      )}

      {/* Empty State */}
      {!isLoading && codes.length === 0 && !error && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <div className="rounded-full bg-muted p-4 mb-4">
              <FileCode className="h-12 w-12 text-muted-foreground" />
            </div>
            <CardTitle className="mb-2">{t("history.empty.title")}</CardTitle>
            <CardDescription className="max-w-md mb-6">
              {t("history.empty.description")}
            </CardDescription>
            {onCreateNew && (
              <Button onClick={onCreateNew} size="lg">
                <Plus className="h-4 w-4 mr-2" />
                {t("codeGenerator.createNewCode")}
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Codes List */}
      {!isLoading && codes.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <p className="text-sm text-muted-foreground">
                {t("history.resultsCount", { count: codes.length })}
              </p>
              {hasActiveFilters && (
                <>
                  <Separator orientation="vertical" className="h-4" />
                  <Badge variant="secondary" className="text-xs">
                    {t("history.filters.filtered", "Filtré")}
                  </Badge>
                </>
              )}
            </div>
          </div>

          <Separator />

          <div className="flex flex-wrap gap-4  justify-center">
            {codes.map((code) => (
              <CodeHistoryCard
                key={code.id}
                code={code}
                onDelete={handleDelete}
                onUpdate={handleUpdate}
                onEdit={onEdit}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
